import { Patient } from '@xolv-mybrightlink/common';
import { isEmpty } from 'lodash';
import { ProgressPortrait } from '../model/ProgressPortrait';
import { CareEpisode } from '../model/CareEpisode';

export type PatientProgress = Pick<Patient, 'sfId' | 'firstName' | 'lastName'>;

type FetchType<T> = 'not-load' | 'loading' | T | 'error';

type CareEpisodeWithProgressPortrait = CareEpisode & {
  errorMessage: string;
  progressPortrait: FetchType<ProgressPortrait>;
};

type PatientProgressPortrait = {
  patientSfId: string;
  patientFirstName: string;
  patientLastName: string;
  selectedAbaCaseSfId: string | null;
  careEpisodes: FetchType<CareEpisodeWithProgressPortrait[]>;
  errorMessage: string;
};

export class ProgressPortraitStore {
  public selectedPatientSfId: string;
  public patientProgressPortraitData: PatientProgressPortrait[];

  constructor(
    selectedPatientSfId: string,
    patientProgressPortraitData: PatientProgressPortrait[],
  ) {
    this.selectedPatientSfId = selectedPatientSfId;
    this.patientProgressPortraitData = patientProgressPortraitData;
  }

  static init(patients: Patient[]): ProgressPortraitStore {
    if (isEmpty(patients)) {
      throw new Error(
        'Cannot init state for progress portrait. Patient list is empty',
      );
    }
    const selectedPatientSfId = patients[0].sfId;
    const patientProgressPortraits: PatientProgressPortrait[] = patients.map(
      (patient) =>
        ({
          patientSfId: patient.sfId,
          patientFirstName: patient.firstName,
          selectedAbaCaseSfId: null,
          patientLastName: patient.lastName,
          careEpisodes: 'not-load',
          errorMessage: '',
        } as PatientProgressPortrait),
    );
    return new ProgressPortraitStore(
      selectedPatientSfId,
      patientProgressPortraits,
    );
  }

  public patientList(): PatientProgress[] {
    return this.patientProgressPortraitData.map((patient) => ({
      sfId: patient.patientSfId,
      firstName: patient.patientFirstName,
      lastName: patient.patientLastName,
    }));
  }

  public getSelectedAbaCaseSfId(patientSfId: string) {
    const patient = this.patientProgressPortraitData.find(
      (patient) => patient.patientSfId === patientSfId,
    );
    if (patient) {
      return patient.selectedAbaCaseSfId;
    }
    return null;
  }

  public selectTreatmentPeriod(
    patientSfId: string,
    selectedAbaCaseSfId: string,
  ) {
    const patient = this.patientProgressPortraitData.find(
      (patient) => patient.patientSfId === patientSfId,
    );
    if (patient) {
      patient.selectedAbaCaseSfId = selectedAbaCaseSfId;
    }
  }

  public fetchingCareEpisode(patientSfId: string) {
    const patient = this.patientProgressPortraitData.find(
      (p) => p.patientSfId === patientSfId,
    );
    if (patient) {
      patient.careEpisodes = 'loading';
      patient.errorMessage = '';
    }
  }

  public fetchedCareEpisode(patientSfId: string, careEpisodes: CareEpisode[]) {
    const patient = this.patientProgressPortraitData.find(
      (p) => p.patientSfId === patientSfId,
    );
    if (patient) {
      patient.careEpisodes = careEpisodes.map((episode) => ({
        ...episode,
        progressPortrait: 'not-load',
        errorMessage: '',
      }));
      if (!patient.selectedAbaCaseSfId && !isEmpty(careEpisodes)) {
        patient.selectedAbaCaseSfId = careEpisodes[0].abaCaseId;
      }
    }
  }

  public errorFetchCareEpisode(patientSfId: string, errorMessage: string) {
    const patient = this.patientProgressPortraitData.find(
      (p) => p.patientSfId === patientSfId,
    );
    if (patient) {
      patient.careEpisodes = 'error';
      patient.errorMessage = errorMessage;
    }
  }

  public loadingProgressPortrait(patientSfId: string, caseSfId: string) {
    const patient = this.patientProgressPortraitData.find(
      (progressPortrait) => progressPortrait.patientSfId === patientSfId,
    );
    if (patient && Array.isArray(patient.careEpisodes)) {
      const progressPortrait = patient.careEpisodes.find(
        (episode) => episode.abaCaseId === caseSfId,
      );
      if (progressPortrait) {
        progressPortrait.progressPortrait = 'loading';
      }
    }
  }

  public fetchedProgressPortrait(
    patientSfId: string,
    caseSfId: string,
    fetchedProgressPortraitData: ProgressPortrait,
  ) {
    const patient = this.patientProgressPortraitData.find(
      (progressPortrait) => progressPortrait.patientSfId === patientSfId,
    );
    if (patient && Array.isArray(patient.careEpisodes)) {
      const progressPortrait = patient.careEpisodes.find(
        (episode) => episode.abaCaseId === caseSfId,
      );
      if (progressPortrait) {
        progressPortrait.progressPortrait = fetchedProgressPortraitData;
        progressPortrait.errorMessage = '';
      }
    }
  }

  public errorFetchProgressPortrait(
    patientSfId: string,
    caseSfId: string,
    errorMessage: string,
  ) {
    const patient = this.patientProgressPortraitData.find(
      (progressPortrait) => progressPortrait.patientSfId === patientSfId,
    );
    if (patient && Array.isArray(patient.careEpisodes)) {
      const progressPortrait = patient.careEpisodes.find(
        (episode) => episode.abaCaseId === caseSfId,
      );
      if (progressPortrait) {
        progressPortrait.progressPortrait = 'error';
        progressPortrait.errorMessage = errorMessage;
      }
    }
  }

  public newState(selectedPatientSfId?: string): ProgressPortraitStore {
    return new ProgressPortraitStore(
      selectedPatientSfId ?? this.selectedPatientSfId,
      this.patientProgressPortraitData,
    );
  }

  public getCareEpisodes(selectedPatientSfId: string) {
    return this.patientProgressPortraitData.find(
      (patient) => patient.patientSfId === selectedPatientSfId,
    )?.careEpisodes;
  }

  public getProgressPortraitData(
    selectedPatientSfId: string,
    selectedAbaCaseSfId: string,
  ) {
    const patient = this.patientProgressPortraitData.find(
      (patient) => patient.patientSfId === selectedPatientSfId,
    );
    if (patient && Array.isArray(patient.careEpisodes)) {
      return patient.careEpisodes.find(
        (episode) => episode.abaCaseId === selectedAbaCaseSfId,
      )?.progressPortrait;
    }
  }
}
